import { useState, ChangeEvent } from 'react';
import { SignInFormData, SignUpFormData, MfaFormData } from '../../types/auth';

// Hook for sign in form
export const useSignInForm = (defaultValues?: Partial<SignInFormData>) => {
  const [formData, setFormData] = useState<SignInFormData>({
    email: defaultValues?.email || '',
    password: defaultValues?.password || '',
    rememberMe: defaultValues?.rememberMe || false
  });

  const handleChange = (e: ChangeEvent<HTMLInputElement>): void => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  return {
    formData,
    setFormData,
    handleChange
  };
};

// Hook for sign up form
export const useSignUpForm = (defaultOrgId: string = '', setOrgIdCallback?: (id: string) => void) => {
  const [formData, setFormData] = useState<SignUpFormData>({
    firstName: '',
    lastName: '',
    state: '',
    email: '',
    password: '',
    confirmPassword: '',
    orgId: defaultOrgId,
    organizationName: '',
    plan: 'core',
    acceptTerms: false,
    fictionalDataConsent: false
  });
  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>): void => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    
    if (name === 'orgId' && setOrgIdCallback) {
      setOrgIdCallback(value);
    }
    
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  return {
    formData,
    setFormData,
    handleChange
  };
};

// Hook for MFA code form
export const useMfaForm = () => {
  const [formData, setFormData] = useState<MfaFormData>({
    code: ''
  });

  const handleChange = (e: ChangeEvent<HTMLInputElement>): void => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return {
    formData,
    setFormData,
    handleChange
  };
};

// Hook for auth error and loading state
export const useAuthState = () => {
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  return {
    error,
    setError,
    isLoading, 
    setIsLoading
  };
};

// Combined hook for all auth forms
export const useAuthForms = () => {
  const { formData: signinData, setFormData: setSigninData, handleChange: handleSigninChange } = useSignInForm();
  const { formData: signupData, handleChange: handleSignupChange } = useSignUpForm();
  const { formData: mfaData, handleChange: handleMfaChange } = useMfaForm();

  return {
    signinData,
    signupData,
    mfaData,
    handleSigninChange,
    handleSignupChange,
    handleMfaChange,
    /** Prefill the sign-in email (after verification or a reset) and clear the password */
    setSigninEmail: (email: string) => setSigninData((prev) => ({ ...prev, email, password: '' }))
  };
};
